import { Box } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RunAdvisorInsights } from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { buildRunTopTickerRollups } from '../../utils/insights/buildRunTopTickerRollups';
import {
  formatRunTradeMixMeterCopy,
  formatRunTurnoverMeterCopy,
  partitionRunImpactNotes,
  shouldHideRunCashEventFlags,
  shouldShowRunTradeMix,
  shouldShowRunTurnover,
} from '../../utils/presentation/formatRunImpactSummary';
import {
  InsightCashEventBadges,
  InsightMetricCard,
  InsightMetricsRow,
  InsightTaxSellSplitBar,
  InsightTickerAmountGrid,
  InsightTickerGridsRow,
  InsightTradeMixMeter,
  InsightTurnoverMeter,
} from '../shared/insightVisuals';

export type BulkRebalanceRunImpactSectionProps = {
  insights: RunAdvisorInsights;
  audits?: OptimizerAuditV2Record[];
  tickerMap?: Record<string, string>;
};

export const BulkRebalanceRunImpactSection = ({
  insights,
  audits = [],
  tickerMap = {},
}: BulkRebalanceRunImpactSectionProps) => {
  const { t } = useTranslation(['bulkRebalance']);

  const { cashEventNotes, otherNotes } = useMemo(
    () => partitionRunImpactNotes(insights.situationCounts),
    [insights.situationCounts],
  );

  const turnoverCopy = useMemo(
    () =>
      shouldShowRunTurnover(insights)
        ? formatRunTurnoverMeterCopy(insights, t)
        : null,
    [insights, t],
  );

  const tradeMixCopy = useMemo(
    () =>
      shouldShowRunTradeMix(insights)
        ? formatRunTradeMixMeterCopy(insights, t)
        : null,
    [insights, t],
  );

  const topTickerRollups = useMemo(
    () => buildRunTopTickerRollups(audits, tickerMap),
    [audits, tickerMap],
  );

  const showTaxBlock = insights.portfoliosWithSells > 0;
  const showTurnover = turnoverCopy != null;
  const showTradeMix = tradeMixCopy != null;
  const hideCashFlags = shouldHideRunCashEventFlags(insights, showTradeMix);
  const flagNotes = [...(hideCashFlags ? [] : cashEventNotes), ...otherNotes];
  const showFlags = flagNotes.length > 0;
  const showMetrics = showTaxBlock || showTurnover || showTradeMix;
  const showTaxTickerGrids =
    topTickerRollups.topTaxableSells.length > 0 ||
    topTickerRollups.topShelteredSells.length > 0;
  const showTradeMixTickerGrids =
    showTradeMix &&
    (topTickerRollups.topBuys.length > 0 ||
      topTickerRollups.topSells.length > 0);

  if (!showMetrics && !showFlags) {
    return null;
  }

  return (
    <Box
      data-testid='bulk-rebalance-run-impact-section'
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {showFlags ? (
        <InsightCashEventBadges
          notes={flagNotes}
          t={t}
          testId='bulk-rebalance-run-impact-flags'
        />
      ) : null}

      {showMetrics ? (
        <InsightMetricsRow testId='bulk-rebalance-run-impact-metrics'>
          {showTaxBlock ? (
            <InsightMetricCard
              title={t('runInsights.impact.taxTitle')}
              framed={false}
              testId='bulk-rebalance-run-impact-tax'
            >
              <InsightTaxSellSplitBar
                registeredSellCents={insights.registeredSellCents}
                taxableSellCents={insights.taxableSellCents}
                estRealizedGainCents={insights.estRealizedGainCents}
                estRealizedGainIsComplete={insights.estRealizedGainIsComplete}
                incompleteGainLineCount={insights.incompleteGainLineCount}
                topTaxableSells={topTickerRollups.topTaxableSells}
                topShelteredSells={topTickerRollups.topShelteredSells}
                includeTickerGrids={false}
                testId='bulk-rebalance-run-impact-tax-split'
              />
            </InsightMetricCard>
          ) : null}

          {showTurnover && turnoverCopy ? (
            <InsightMetricCard
              title={t('runInsights.impact.turnoverTitle')}
              framed={false}
              testId='bulk-rebalance-run-impact-turnover'
            >
              <InsightTurnoverMeter
                turnoverPct={insights.turnoverMedianPct ?? 0}
                caption={turnoverCopy.caption}
                footnote={turnoverCopy.footnote}
                detailLine={turnoverCopy.detailLine}
                testId='bulk-rebalance-run-impact-turnover-meter'
              />
            </InsightMetricCard>
          ) : null}

          {showTradeMix && tradeMixCopy ? (
            <InsightMetricCard
              title={t('runInsights.impact.tradeMixTitle')}
              framed={false}
              testId='bulk-rebalance-run-impact-trade-mix'
            >
              <InsightTradeMixMeter
                buyCents={tradeMixCopy.buyCents}
                sellCents={tradeMixCopy.sellCents}
                centerLabel={tradeMixCopy.centerLabel}
                caption={tradeMixCopy.caption}
                includeTickerGrids={false}
                testId='bulk-rebalance-run-impact-trade-mix-meter'
              />
            </InsightMetricCard>
          ) : null}
        </InsightMetricsRow>
      ) : null}

      {showTaxTickerGrids ? (
        <InsightTickerGridsRow testId='bulk-rebalance-run-impact-ticker-grids'>
          <InsightTickerAmountGrid
            title={t('runInsights.impact.topTaxableSellsTitle')}
            rows={topTickerRollups.topTaxableSells}
            testId='bulk-rebalance-run-impact-tax-split-taxable-grid'
          />
          <InsightTickerAmountGrid
            title={t('runInsights.impact.topShelteredSellsTitle')}
            rows={topTickerRollups.topShelteredSells}
            testId='bulk-rebalance-run-impact-tax-split-sheltered-grid'
          />
        </InsightTickerGridsRow>
      ) : null}

      {showTradeMixTickerGrids ? (
        <InsightTickerGridsRow testId='bulk-rebalance-run-impact-trade-mix-grids'>
          <InsightTickerAmountGrid
            title={t('runInsights.impact.topBatchSellsTitle')}
            rows={topTickerRollups.topSells}
            testId='bulk-rebalance-run-impact-trade-mix-meter-sells-grid'
          />
          <InsightTickerAmountGrid
            title={t('runInsights.impact.topBatchBuysTitle')}
            rows={topTickerRollups.topBuys}
            testId='bulk-rebalance-run-impact-trade-mix-meter-buys-grid'
          />
        </InsightTickerGridsRow>
      ) : null}
    </Box>
  );
};
